import React, { CSSProperties, useState } from "react";
import { useDispatch } from "react-redux";
import "./OverlayWellControl.css";
import ImagerApi from "../../api/ImagerApi";
import { setShowWellOverlay } from "../../redux/actions/LiveImageAction";

type Props = {
  numberOfCol: number;
  numberOfRow: number;
  selectedCol: number;
  selectedRow: number;
  drop: number;
  imagerIdentifier: string;
  style?: CSSProperties;
};

export default function OverlayWellControl({
  numberOfCol,
  numberOfRow,
  selectedCol,
  selectedRow,
  drop,
  imagerIdentifier,
  style = {},
}: Props) {
  const dispatch = useDispatch();

  const [hoveredCell, setHoveredCell] = useState<[number, number] | null>(null);
  const handleClick = (cell: [number, number]) => {
    const wellNumber = cell[0] * numberOfCol + cell[1] + 1;
    ImagerApi.moveToWell(imagerIdentifier, wellNumber, drop);
  };

  const handleMouseEnter = (cell: [number, number]) => {
    setHoveredCell(cell);
  };

  const handleMouseExit = (cell: [number, number]) => {
    const isHovered =
      hoveredCell && hoveredCell[0] === cell[0] && hoveredCell[1] === cell[1];
    if (isHovered) setHoveredCell(null);
  };

  const renderRowHeader = () => {
    const cells = [];
    for (let col = -1; col < numberOfCol; col++) {
      cells.push(renderRowHeaderCell(col));
    }

    return (
      <div key={`owc-row-headers`} className="owc-row-headers">
        {cells}
      </div>
    );
  };

  const renderRowHeaderCell = (col: number) => {
    const iConnected = selectedCol === col;
    const isHoveredConnected = hoveredCell && hoveredCell[1] === col;
    const isEmpty = col === -1;

    return (
      <div
        key={`owc-row-header-${col}`}
        className={`owc-row-header 
          ${iConnected ? "selected" : ""} 
          ${isHoveredConnected ? "hovered" : ""} 
          ${isEmpty ? "empty" : ""}`}
      >
        {col + 1}
      </div>
    );
  };

  const getColHeaderNameFromNumber = (num: number): string => {
    const numeric = num % 26;
    const letter = String.fromCharCode(65 + numeric);
    const num2 = Math.floor(num / 26);
    if (num2 > 0) {
      return getColHeaderNameFromNumber(num2 - 1) + letter;
    } else {
      return letter;
    }
  };

  const renderColHeader = (row: number) => {
    const iConnected = selectedRow === row;

    const isHoveredConnected = hoveredCell && hoveredCell[0] === row;

    return (
      <div
        key={`owc-col-header-${row}`}
        className={`owc-col-header 
          ${iConnected ? "selected" : ""} 
          ${isHoveredConnected ? "hovered" : ""}`}
      >
        {getColHeaderNameFromNumber(row)}
      </div>
    );
  };

  const renderCell = (row: number, col: number) => {
    const isSelected = selectedRow === row && selectedCol === col;
    const isHovered =
      hoveredCell && hoveredCell[0] === row && hoveredCell[1] === col;
    const isConnected =
      !isSelected &&
      !isHovered &&
      (selectedRow === row || selectedCol === col) &&
      (selectedRow > row || selectedCol > col);

    const isHoveredConnected =
      !isSelected &&
      !isHovered &&
      hoveredCell &&
      (hoveredCell[0] === row || hoveredCell[1] === col) &&
      (hoveredCell[0] > row || hoveredCell[1] > col);

    return (
      <div
        key={`${row}${col}`}
        className={`owc-cell 
          ${isSelected ? "selected" : ""} 
          ${isHovered ? "hovered" : ""} 
          ${isConnected ? "connected" : ""} 
          ${isHoveredConnected ? "hovered-connected" : ""}`}
        onClick={() => handleClick([row, col])}
        onMouseEnter={() => handleMouseEnter([row, col])}
        onMouseLeave={() => handleMouseExit([row, col])}
      />
    );
  };

  const renderRow = (row: number) => {
    const cells = [];

    cells.push(renderColHeader(row));

    for (let col = 0; col < numberOfCol; col++) {
      cells.push(renderCell(row, col));
    }

    return (
      <div key={row} className="owc-row">
        {cells}
      </div>
    );
  };

  const onClose = () => {
    dispatch(setShowWellOverlay(false));
  };

  const rows = [];

  rows.push(renderRowHeader());

  for (let row = 0; row < numberOfRow; row++) {
    rows.push(renderRow(row));
  }

  const well = String.fromCharCode(selectedRow + 65) + (selectedCol + 1);

  return (
    <div style={style} className="owc-container">
      <div className="owc-header">
        <div className="owc-header-icon" onClick={onClose}>
          <svg
            width="24"
            height="25"
            viewBox="0 0 24 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5 3.5C3.89543 3.5 3 4.39543 3 5.5V19.5C3 20.6046 3.89543 21.5 5 21.5H19C20.1046 21.5 21 20.6046 21 19.5V5.5C21 4.39543 20.1046 3.5 19 3.5H5ZM8 18.5L12 14.5L16 18.5L18 16.5L14 12.5L18 8.5L16 6.5L12 10.5L8 6.5L6 8.5L10 12.5L6 16.5L8 18.5Z"
              fill="#F7A246"
            />
          </svg>
        </div>
      </div>
      <div className="owc-chessboard">{rows}</div>
    </div>
  );
}
